import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import { categories } from "@/mocks/categories";

const principles = [
  { icon: "ri-focus-3-line", title: "题材聚焦", desc: "每个专题只做同类佳作，不堆砌无关内容，确保读者快速定位偏好题材。" },
  { icon: "ri-check-double-line", title: "真实导览", desc: "认真撰写简介与状态标注，避免标题党，让每一部作品都经得起阅读。" },
  { icon: "ri-refresh-line", title: "持续更新", desc: "按韩漫大全分类专题定位持续补充新作与完结佳作，让专题保持新鲜。" },
  { icon: "ri-heart-line", title: "读者优先", desc: "以读者找书体验为核心，精选口碑与人气兼具的作品，尊重每位读者的口味。" },
];

function About() {
  return (
    <Layout>
      <section className="relative overflow-hidden bg-foreground-950 py-24">
        <div
          className="pointer-events-none absolute inset-0 opacity-25"
          style={{
            backgroundImage:
              "radial-gradient(circle at 30% 30%, oklch(var(--primary-500) / 0.6), transparent 45%), radial-gradient(circle at 75% 70%, oklch(var(--accent-500) / 0.5), transparent 45%)",
          }}
          aria-hidden="true"
        />
        <div className="relative mx-auto max-w-7xl px-4 md:px-6">
          <nav aria-label="面包屑" className="flex items-center gap-2 text-sm text-background-400">
            <Link to="/" className="transition-colors hover:text-accent-300">首页</Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true" />
            <span className="text-background-200">关于我们</span>
          </nav>
          <h1 className="mt-6 font-heading text-3xl font-bold text-background-50 md:text-4xl">
            关于韩国漫画大全
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">
            韩国漫画大全是一个按题材分类整理的韩漫推荐门户，专注帮助中文读者高效发现优质韩国漫画。
          </p>
        </div>
      </section>

      <section className="bg-background-50 py-16 md:py-20">
        <div className="mx-auto grid max-w-7xl items-start gap-10 px-4 md:grid-cols-2 md:px-6">
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">我们为什么做这个网站</h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              韩国漫画近年佳作层出不穷，但题材庞杂、风格各异，很多人面对海量作品常常无从下手。
              我们创建韩国漫画大全，就是想用「分类专题」的方式，把不同题材的好作品整理成清晰的地图——
              奇幻的走热血冒险，校园的走青春恋爱，古风的走华美意境，让读者总能顺着自己的偏好找到下一部想追的作品。
            </p>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              这不是一个简单的作品列表，而是一份持续生长的韩漫题材导览。我们希望每个专题都能成为读者「按类型找韩漫」的首选入口。
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              {categories.map((c) => (
                <Link
                  key={c.slug}
                  to={`/category/${c.slug}`}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-background-100 px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-primary-100 hover:text-primary-700"
                >
                  <i className={c.icon} aria-hidden="true" />
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-100 p-6">
            <h3 className="font-heading text-lg font-bold text-foreground-950">我们的内容原则</h3>
            <ul className="mt-5 space-y-4">
              {principles.map((p) => (
                <li key={p.title} className="flex gap-3.5">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background-50 text-primary-600">
                    <i className={`${p.icon} text-xl`} aria-hidden="true" />
                  </span>
                  <div>
                    <div className="text-sm font-semibold text-foreground-900">{p.title}</div>
                    <p className="mt-1 text-xs leading-relaxed text-foreground-600">{p.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="grid gap-5 md:grid-cols-3">
            {[
              { icon: "ri-crosshair-2-line", title: "网站定位", desc: "韩漫题材发现与推荐门户，覆盖奇幻、校园、古风等专题，让「按类型找韩漫」变得更简单。" },
              { icon: "ri-compass-3-line", title: "发展理念", desc: "持续扩充与优化专题内容，与读者一起发现更多值得被看见的优质韩国漫画。" },
              { icon: "ri-team-line", title: "与读者同行", desc: "重视每位读者的反馈与推荐，让专题更贴近真实阅读偏好，做有温度的韩漫导览。" },
            ].map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-7">
                <span className="flex h-12 w-12 items-center justify-center rounded-md bg-accent-500 text-foreground-950">
                  <i className={`${item.icon} text-xl`} aria-hidden="true" />
                </span>
                <h3 className="mt-5 font-heading text-lg font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="flex flex-col items-center justify-between gap-6 rounded-lg bg-primary-500 px-8 py-10 text-center md:flex-row md:text-left">
            <div>
              <h2 className="font-heading text-xl font-bold text-background-50 md:text-2xl">想和我们聊聊或推荐作品？</h2>
              <p className="mt-2 text-sm text-primary-100">
                欢迎通过联系我们页面留言，我们很乐意倾听你的意见与推荐。
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-6 py-3 text-sm font-semibold text-primary-700 transition-colors hover:bg-background-100"
            >
              联系我们
              <i className="ri-arrow-right-line" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}

export default About;